import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAnalytics, getExplorerData, getPolygonsForGroups, getHistoryAttributeValues, postGroupComparison } from '../services/apiClient';
import { endpoints } from '../services/apiClient';
import {
  PolygonData,
  PolygonGroup,
  AnnualNEEData,
  MonthlyNEEData,
  GroupComparisonResponse,
  ComparisonGroupDefinition,
  GroupComparisonRequest,
  KpiData,
} from '../types/schema';
import { GroupingOption, SortingOption } from '../types/enums';
import { RootState } from './store';

interface AnalyticsData {
  annualData: AnnualNEEData[];
  monthlyData: MonthlyNEEData[];
  kpiData: KpiData;
}

const sanitizeComparisonGroup = (definition: ComparisonGroupDefinition): ComparisonGroupDefinition => {
  const cleanedFilters: { tags?: string[]; historyFilters?: Record<string, string[]> } = {};

  if (definition.filters.tags && definition.filters.tags.length > 0) {
    cleanedFilters.tags = definition.filters.tags;
  }

  if (definition.filters.historyFilters) {
    const compactHistory = Object.entries(definition.filters.historyFilters).reduce<Record<string, string[]>>(
      (acc, [key, values]) => {
        if (values && values.length > 0) {
          acc[key] = values;
        }
        return acc;
      },
      {}
    );
    if (Object.keys(compactHistory).length > 0) {
      cleanedFilters.historyFilters = compactHistory;
    }
  }

  return {
    label: definition.label,
    filters: cleanedFilters,
  };
};

export interface DataExplorerState {
  searchQuery: string;
  groupingOption: GroupingOption;
  sortingOption: SortingOption;
  startYear: number;
  endYear: number;
  months: number[];
  selectedTags: string[];
  selectedPolygonIds: (string | number)[];
  selectedGroupIds: string[];
  polygons: PolygonData[];
  polygonGroups: PolygonGroup[];
  groupedPolygonDetails: PolygonData[];
  analytics: AnalyticsData | null;
  historyFilters: Record<string, string[]>;
  historyAttributeValues: Record<string, string[]>;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  groupDetailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  groupComparisonGroups: ComparisonGroupDefinition[];
  groupComparisonResult: GroupComparisonResponse | null;
  groupComparisonStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  groupComparisonError: string | null;
  isYearRangeFinal: boolean;
  historyAttributeStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  minAvailableYear: number | null;
  maxAvailableYear: number | null;
  hasInitializedYear: boolean | false
}

const initialState: DataExplorerState = {
  searchQuery: '',
  groupingOption: GroupingOption.NONE,
  sortingOption: SortingOption.NAME_ASC,
  startYear: 2020,
  endYear: new Date().getFullYear(),
  months: [],
  selectedTags: [],
  selectedPolygonIds: [],
  selectedGroupIds: [],
  polygons: [],
  polygonGroups: [],
  groupedPolygonDetails: [],
  analytics: null,
  historyFilters: {},
  historyAttributeValues: {},
  status: 'idle',
  groupDetailsStatus: 'idle',
  error: null,
  groupComparisonGroups: [],
  groupComparisonResult: null,
  groupComparisonStatus: 'idle',
  groupComparisonError: null,
  isYearRangeFinal: false,
  historyAttributeStatus: 'idle',
  minAvailableYear: null,
  maxAvailableYear: null,
  hasInitializedYear: false,
};

export const fetchExplorerData = createAsyncThunk(
  'dataExplorer/fetchExplorerData',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { dataExplorer, app } = state;
    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    const response = await getExplorerData({
      grouping: dataExplorer.groupingOption,
      start_year: dataExplorer.startYear,
      end_year: dataExplorer.endYear,
      months: dataExplorer.months,
      search: dataExplorer.searchQuery,
      tags: dataExplorer.selectedTags,
      history_filters: dataExplorer.historyFilters,
    }, accountId);
    return response;
  }
);

export const fetchAnalyticsData = createAsyncThunk(
  'dataExplorer/fetchAnalyticsData',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { dataExplorer, app } = state;
    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    const response = await getAnalytics({
      startYear: dataExplorer.startYear,
      endYear: dataExplorer.endYear,
      months: dataExplorer.months,
      polygonIds: dataExplorer.selectedPolygonIds as string[],
      groupIds: dataExplorer.selectedGroupIds,
      groupBy: dataExplorer.groupingOption,
      tags: dataExplorer.selectedTags,
      history_filters: dataExplorer.historyFilters,
    }, accountId);
    return response;
  }
);

export const fetchPolygonsForGroups = createAsyncThunk(
  'dataExplorer/fetchPolygonsForGroups',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { dataExplorer, app } = state;
    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    // Get all polygon IDs from the selected groups
    const polygonIds = dataExplorer.selectedGroupIds.flatMap(groupId => {
      const group = dataExplorer.polygonGroups.find(g => g.id === groupId);
      return group ? group.polygonIds : [];
    });

    // Remove duplicates
    const uniquePolygonIds = [...new Set(polygonIds)];

    if (uniquePolygonIds.length === 0) {
      return [];
    }

    // Fetch polygons with NEE data using year range and months from state
    const response = await getPolygonsForGroups(
      uniquePolygonIds,
      dataExplorer.startYear,
      dataExplorer.endYear,
      dataExplorer.months.length > 0 ? dataExplorer.months : undefined,
      accountId
    );
    return response;
  }
);

export const fetchAvailableYears = createAsyncThunk(
  'dataExplorer/fetchAvailableYears',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { app } = state;
    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    const response = await endpoints.dashboardAvailableYears(accountId);
    return response;
  }
);

export const fetchHistoryAttributeValues = createAsyncThunk(
  'dataExplorer/fetchHistoryAttributeValues',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { app } = state;
    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    const ATTRIBUTES = ['land_cover', 'crop', 'primary_practice', 'secondary_practice', 'sourcing_year'];
    const response = await getHistoryAttributeValues(ATTRIBUTES, accountId);
    return response;
  }
);

export const fetchGroupComparisonData = createAsyncThunk<
  GroupComparisonResponse,
  void,
  { state: RootState; rejectValue: string }
>(
  'dataExplorer/fetchGroupComparisonData',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { dataExplorer, app } = state;

    if (dataExplorer.groupComparisonGroups.length !== 2) {
      return rejectWithValue('Two groups must be defined for comparison.');
    }

    const request: GroupComparisonRequest = {
      startYear: dataExplorer.startYear,
      endYear: dataExplorer.endYear,
      groups: dataExplorer.groupComparisonGroups.map(sanitizeComparisonGroup),
    };

    if (dataExplorer.months.length > 0) {
      request.months = dataExplorer.months;
    }

    const { isAdmin, selectedAccount } = app;
    const accountId = isAdmin ? selectedAccount?.accountId : undefined;

    try {
      return await postGroupComparison(request, accountId);
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch group comparison.');
    }
  }
);

const dataExplorerSlice = createSlice({
  name: 'dataExplorer',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      if (!action.payload) {
        state.selectedPolygonIds = [];
        state.selectedGroupIds = [];
      }
    },
    setGroupingOption: (state, action: PayloadAction<GroupingOption>) => {
      state.groupingOption = action.payload;
      state.selectedPolygonIds = [];
      state.selectedGroupIds = [];
    },
    setSortingOption: (state, action: PayloadAction<SortingOption>) => {
      state.sortingOption = action.payload;
    },
    setYearRange: (state, action: PayloadAction<{ startYear: number; endYear: number }>) => {
      state.startYear = action.payload.startYear;
      state.endYear = action.payload.endYear;
    },
    setMonths: (state, action: PayloadAction<number[]>) => {
      state.months = action.payload;
    },
    setHasInitializedYear: (state, action: PayloadAction<boolean>) => {
          state.hasInitializedYear = action.payload;
    },
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    setHistoryFilters: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.historyFilters = action.payload;
    },
    setHistoryAttributeValues: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.historyAttributeValues = action.payload;
    },
    setGroupComparisonGroups: (state, action: PayloadAction<ComparisonGroupDefinition[]>) => {
      state.groupComparisonGroups = action.payload;
      state.groupComparisonResult = null;
      state.groupComparisonStatus = 'idle';
      state.groupComparisonError = null;
    },
    clearGroupComparisonResult: (state) => {
      state.groupComparisonResult = null;
      state.groupComparisonStatus = 'idle';
      state.groupComparisonError = null;
    },
    togglePolygonSelection: (state, action: PayloadAction<string | number>) => {
      const id = action.payload;
      if (state.selectedPolygonIds.includes(id)) {
        state.selectedPolygonIds = state.selectedPolygonIds.filter(pId => pId !== id);
      } else {
        state.selectedPolygonIds.push(id);
      }
    },
    toggleGroupSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedGroupIds.includes(id)) {
        state.selectedGroupIds = state.selectedGroupIds.filter(gId => gId !== id);
      } else {
        state.selectedGroupIds.push(id);
      }
    },
    selectAllPolygons: (state, action: PayloadAction<(string|number)[]>) => {
        state.selectedPolygonIds = action.payload;
    },
    selectAllGroups: (state, action: PayloadAction<string[]>) => {
        state.selectedGroupIds = action.payload;
    },
    deselectAllPolygons: (state) => {
        state.selectedPolygonIds = [];
    },
    deselectAllGroups: (state) => {
        state.selectedGroupIds = [];
        state.groupedPolygonDetails = []; // Clear details when all groups are deselected
    },
    clearSearchAndPolygonData: (state) => {
      state.searchQuery = '';
      state.polygons = [];
      state.polygonGroups = [];
      state.groupedPolygonDetails = [];
      state.selectedPolygonIds = [];
      state.selectedGroupIds = [];
      state.groupComparisonGroups = [];
      state.groupComparisonResult = null;
      state.groupComparisonStatus = 'idle';
      state.groupComparisonError = null;
    },
    resetAllFilters: (state) => {
      state.searchQuery = '';
      state.selectedTags = [];
      state.months = [];
      state.historyFilters = {};
      // Reset to min/max available years if available, otherwise use defaults
      state.startYear = state.minAvailableYear ?? 2020;
      state.endYear = state.maxAvailableYear ?? new Date().getFullYear();
      state.groupingOption = GroupingOption.NONE;
      state.selectedPolygonIds = [];
      state.selectedGroupIds = [];
      state.groupComparisonGroups = [];
      state.groupComparisonResult = null;
      state.groupComparisonStatus = 'idle';
      state.groupComparisonError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExplorerData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchExplorerData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.groupingOption === GroupingOption.NONE) {
            state.polygons = action.payload as PolygonData[];
            state.polygonGroups = [];
            if (state.searchQuery) {
              state.selectedPolygonIds = state.polygons.map(p => p.id);
            }
        } else {
            // Backend doesn't return 'id' field, so we need to add it based on group name
            const groups = action.payload as Omit<PolygonGroup, 'id'>[];
            state.polygonGroups = groups.map(group => ({
              ...group,
              id: group.name // Use name as ID since it's unique per group
            }));
            state.polygons = [];
        }
      })
      .addCase(fetchExplorerData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.analytics = action.payload;
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(fetchPolygonsForGroups.pending, (state) => {
        state.groupDetailsStatus = 'loading';
      })
      .addCase(fetchPolygonsForGroups.fulfilled, (state, action) => {
        state.groupDetailsStatus = 'succeeded';
        state.groupedPolygonDetails = action.payload as PolygonData[];
      })
      .addCase(fetchPolygonsForGroups.rejected, (state, action) => {
        state.groupDetailsStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(fetchAvailableYears.fulfilled, (state, action) => {
        const { min_year, max_year } = action.payload;
        // Store min/max available years for reset functionality
        if (min_year !== null && min_year !== undefined) {
          state.minAvailableYear = min_year;
        }
        if (max_year !== null && max_year !== undefined) {
          state.maxAvailableYear = max_year;
        }
        // Set default start and end year from available NEE data if min/max years exist
        if (min_year && max_year) {
          // Store initial defaults for comparison
          const initialStartYear = 2020;
          const initialEndYear = new Date().getFullYear();
          // Update if still at initial default values
          if (state.startYear === initialStartYear && state.endYear === initialEndYear) {
            state.startYear = min_year;
            state.endYear = max_year;
          }
        }
        state.isYearRangeFinal = true;
      })
      .addCase(fetchHistoryAttributeValues.pending, (state) => {
        state.historyAttributeStatus = 'loading';
      })
      .addCase(fetchHistoryAttributeValues.fulfilled, (state, action) => {
        state.historyAttributeValues = action.payload as Record<string, string[]>;
        state.historyAttributeStatus = 'succeeded';
      })
      .addCase(fetchHistoryAttributeValues.rejected, (state) => {
        state.historyAttributeStatus = 'failed';
      })
      .addCase(fetchGroupComparisonData.pending, (state) => {
        state.groupComparisonStatus = 'loading';
        state.groupComparisonError = null;
      })
      .addCase(fetchGroupComparisonData.fulfilled, (state, action) => {
        state.groupComparisonStatus = 'succeeded';
        state.groupComparisonResult = action.payload;
      })
      .addCase(fetchGroupComparisonData.rejected, (state, action) => {
        state.groupComparisonStatus = 'failed';
        state.groupComparisonResult = null;
        state.groupComparisonError = typeof action.payload === 'string'
          ? action.payload
          : action.error.message || null;
      });
  },
});

export const {
  setSearchQuery,
  setGroupingOption,
  setSortingOption,
  setYearRange,
  setMonths,
  setSelectedTags,
  setHistoryFilters,
  setHistoryAttributeValues,
  setGroupComparisonGroups,
  clearGroupComparisonResult,
  togglePolygonSelection,
  toggleGroupSelection,
  selectAllPolygons,
  selectAllGroups,
  deselectAllPolygons,
  deselectAllGroups,
  clearSearchAndPolygonData,
  resetAllFilters,
  setHasInitializedYear,
} = dataExplorerSlice.actions;

export default dataExplorerSlice.reducer;
